import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { UPDATE_PROFILE_MUTATION } from '../../apollo/queries';
import { useAuth } from '../../admin/AuthContext';
import '../css/UserInfoEditPage.css';

const UserInfoEditPageContent = () => {
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const { user, isAuthenticated, updateUser } = useAuth();

    // 사용자 정보 상태 (실제 데이터베이스 값으로 초기화)
    const [userInfo, setUserInfo] = useState({
        loginId: '',
        name: '',
        phone: '',
        company: '',
        email: '',
        // 회사 상세 정보
        ceoName: '',
        zipCode: '',
        address: '',
        addressDetail: '',
        businessNumber: '',
        // 사용자 상세 정보
        department: '',
        mobile: '',
        fax: ''
    });

    // GraphQL 프로필 업데이트 뮤테이션
    const [updateProfile] = useMutation(UPDATE_PROFILE_MUTATION, {
        onCompleted: (data) => {
            if (data.updateProfile) {
                setIsLoading(false);
                // 로컬 사용자 정보 업데이트
                updateUser({
                    ...user,
                    ...data.updateProfile
                });
                setStep(3); // 완료 단계로 이동
            }
        },
        onError: (error) => {
            setIsLoading(false);
            console.error('프로필 업데이트 에러:', error);
            
            let errorMessage = '사용자 정보 수정 중 오류가 발생했습니다.';
            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                errorMessage = error.graphQLErrors[0].message;
            }
            setErrors({ general: errorMessage });
        }
    });

    // 로그인하지 않은 사용자는 홈으로 리다이렉트
    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    // 사용자 정보 로드
    useEffect(() => {
        if (user) {
            setUserInfo({
                loginId: user.email || '',
                name: user.name || '',
                phone: user.phone || '',
                company: user.company || '',
                email: user.email || '',
                // 회사 상세 정보
                ceoName: user.ceoName || '',
                zipCode: user.zipCode || '',
                address: user.address || '',
                addressDetail: user.addressDetail || '',
                businessNumber: user.businessNumber || '',
                // 사용자 상세 정보
                department: user.department || '',
                mobile: user.mobile || '',
                fax: user.fax || ''
            });
        }
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserInfo(prev => ({
            ...prev,
            [name]: value
        }));
        
        // 에러 메시지 클리어
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!userInfo.name.trim()) {
            newErrors.name = '담당자명을 입력해주세요.';
        }
        
        if (!userInfo.phone.trim()) {
            newErrors.phone = '전화번호를 입력해주세요.';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNextStep = async () => {
        if (step === 1) {
            if (validateForm()) {
                setStep(2);
            }
        } else if (step === 2) {
            setIsLoading(true);
            setErrors({});

            try {
                await updateProfile({
                    variables: {
                        input: {
                            name: userInfo.name,
                            phone: userInfo.phone,
                            company: userInfo.company,
                            // 회사 상세 정보
                            ceoName: userInfo.ceoName,
                            zipCode: userInfo.zipCode,
                            address: userInfo.address,
                            addressDetail: userInfo.addressDetail,
                            businessNumber: userInfo.businessNumber,
                            // 사용자 상세 정보
                            department: userInfo.department,
                            mobile: userInfo.mobile,
                            fax: userInfo.fax
                        }
                    }
                });
            } catch (error) {
                // 에러는 onError에서 처리됨
                console.error('프로필 업데이트 실행 에러:', error);
            }
        }
    };

    const handlePrevStep = () => setStep(step - 1);
    const handleNavigate = (path) => navigate(path);

    const renderError = (fieldName) => {
        if (errors[fieldName]) {
            return <div className="uip-error-message" style={{ color: '#ff4444', fontSize: '14px', marginTop: '5px' }}>{errors[fieldName]}</div>;
        }
        return null;
    };

    const renderStepContent = () => {
        switch (step) {
            case 1:
                return (
                    <div className="uip-step-container">
                        {errors.general && (
                            <div className="uip-error-banner" style={{ 
                                color: '#ff4444', 
                                backgroundColor: '#fff5f5',
                                border: '1px solid #ffdddd',
                                borderRadius: '4px',
                                padding: '10px',
                                marginBottom: '20px'
                            }}>
                                {errors.general}
                            </div>
                        )}
                        <div className="uip-form-grid">
                            <div className="uip-form-row">
                                <label>로그인 ID</label>
                                <div className="uip-form-field static">
                                    {userInfo.loginId} 
                                    <span className="uip-id-note">로그인 ID는 변경할 수 없습니다.</span>
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="name">담당자 <span className="uip-required">*</span></label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="name" 
                                        name="name"
                                        value={userInfo.name} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                    {renderError('name')}
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="company">회사명</label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="company" 
                                        name="company"
                                        value={userInfo.company} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="ceoName">대표자명</label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="ceoName" 
                                        name="ceoName"
                                        value={userInfo.ceoName} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="zipCode">우편번호</label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="zipCode" 
                                        name="zipCode"
                                        value={userInfo.zipCode} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="address">기본 주소</label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="address" 
                                        name="address"
                                        value={userInfo.address} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="addressDetail">상세 주소</label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="addressDetail" 
                                        name="addressDetail"
                                        value={userInfo.addressDetail} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="businessNumber">사업자등록번호</label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="businessNumber" 
                                        name="businessNumber"
                                        value={userInfo.businessNumber} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="department">부서/역할</label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="department" 
                                        name="department"
                                        value={userInfo.department} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="email">이메일 주소</label>
                                <div className="uip-form-field">
                                    <input 
                                        type="email" 
                                        id="email" 
                                        value={userInfo.email} 
                                        readOnly 
                                        style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <button 
                                        className="uip-inline-btn" 
                                        onClick={() => handleNavigate('/email-change')}
                                        disabled={isLoading}
                                    >
                                        이메일 주소 변경
                                    </button>
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="phone">전화번호 <span className="uip-required">*</span></label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="phone" 
                                        name="phone"
                                        value={userInfo.phone} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                    {renderError('phone')}
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="mobile">휴대전화</label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="mobile" 
                                        name="mobile"
                                        value={userInfo.mobile} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                            <div className="uip-form-row">
                                <label htmlFor="fax">FAX</label>
                                <div className="uip-form-field">
                                    <input 
                                        type="text" 
                                        id="fax" 
                                        name="fax"
                                        value={userInfo.fax} 
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="uip-actions">
                            <button 
                                className="uip-confirm-btn-large" 
                                onClick={handleNextStep}
                                disabled={isLoading}
                            >
                                {isLoading ? '처리 중...' : '입력 완료'}
                            </button>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="uip-step-container">
                        <div className="uip-summary-grid">
                            <div className="uip-summary-row">
                                <span className="label">로그인 ID</span>
                                <span>{userInfo.loginId}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">담당자</span>
                                <span>{userInfo.name}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">회사명</span>
                                <span>{userInfo.company || '미입력'}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">대표자명</span>
                                <span>{userInfo.ceoName || '미입력'}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">우편번호</span>
                                <span>{userInfo.zipCode || '미입력'}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">기본 주소</span>
                                <span>{userInfo.address || '미입력'}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">상세 주소</span>
                                <span>{userInfo.addressDetail || '미입력'}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">사업자등록번호</span>
                                <span>{userInfo.businessNumber || '미입력'}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">부서/역할</span>
                                <span>{userInfo.department || '미입력'}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">이메일 주소</span>
                                <span>{userInfo.email}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">전화번호</span>
                                <span>{userInfo.phone}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">휴대전화</span>
                                <span>{userInfo.mobile || '미입력'}</span>
                            </div>
                            <div className="uip-summary-row">
                                <span className="label">FAX</span>
                                <span>{userInfo.fax || '미입력'}</span>
                            </div>
                        </div>
                        <div className="uip-actions space-between">
                            <button 
                                className="uip-back-btn" 
                                onClick={handlePrevStep}
                                disabled={isLoading}
                            >
                                돌아가기
                            </button>
                            <button 
                                className="uip-confirm-btn" 
                                onClick={handleNextStep}
                                disabled={isLoading}
                            >
                                {isLoading ? '수정 중...' : '확인'}
                            </button>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="uip-step-container centered-content">
                        <h2 className="uip-final-title">정보 변경이 완료되었습니다.</h2>
                        <div className="uip-actions">
                            <button 
                                className="uip-confirm-btn-large" 
                                onClick={() => navigate('/my-page')}
                            >
                                완료
                            </button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="user-info-edit-page">
            <div className="uip-breadcrumb">
                <span>홈</span> &gt; <span>내 정보</span> &gt; <span className="current">사용자 정보 수정</span>
            </div>
            <h1 className="uip-title">사용자 정보 수정</h1>
            {renderStepContent()}
        </div>
    );
};

const UserInfoEditPage = () => {
    return (
        <UserInfoEditPageContent />
    );
};

export default UserInfoEditPage; 